import { computed, type MaybeRef, unref } from "vue";
import { useInfiniteQuery } from "@tanstack/vue-query";
import { useApi } from "@shopexperts/shared";
import type { AdminLeadQueueQueryParams, AdminLeadQueueRow } from "@shopexperts/legacy-api";

export interface AdminLeadQueueFilters {
  status?: MaybeRef<"pending" | "matched" | "">;
  leadId?: MaybeRef<string>;
  projectId?: MaybeRef<string>;
  projectTitle?: MaybeRef<string>;
  expertId?: MaybeRef<string>;
  clientId?: MaybeRef<string>;
  needsAttention?: MaybeRef<"recent" | "all" | undefined>;
  attentionReason?: MaybeRef<"client" | "expert">;
}

interface UseAdminLeadQueueOptions extends AdminLeadQueueFilters {
  limit?: number;
  enabled?: MaybeRef<boolean>;
}

function parseNumericFilter(value: string | undefined): number | undefined {
  if (!value) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

export function useAdminLeadQueue(options: UseAdminLeadQueueOptions = {}) {
  const api = useApi();
  const limit = options.limit ?? 20;
  const enabled = computed(() => unref(options.enabled) ?? true);

  const normalizedFilters = computed<AdminLeadQueueQueryParams>(() => ({
    limit,
    status: (unref(options.status) || undefined) as "pending" | "matched" | undefined,
    lead_id: parseNumericFilter(unref(options.leadId)),
    project_id: parseNumericFilter(unref(options.projectId)),
    project_title: unref(options.projectTitle) || undefined,
    expert_id: parseNumericFilter(unref(options.expertId)),
    client_id: parseNumericFilter(unref(options.clientId)),
    needs_attention: unref(options.needsAttention) || undefined,
    attention_reason: unref(options.needsAttention) ? unref(options.attentionReason) : undefined,
  }));

  const query = useInfiniteQuery({
    queryKey: computed(() => ["admin", "leads-projects", "queue", normalizedFilters.value]),
    queryFn: async ({ pageParam }) => {
      return await api.admin.leadsV2.listQueue({
        query: {
          ...normalizedFilters.value,
          cursor: pageParam,
        },
      });
    },
    getNextPageParam: (lastPage) => {
      return lastPage.pagination.has_next ? lastPage.pagination.cursor : undefined;
    },
    initialPageParam: undefined as string | undefined,
    enabled,
    staleTime: 15_000,
    gcTime: 5 * 60_000,
  });

  const rows = computed<AdminLeadQueueRow[]>(() =>
    query.data.value?.pages.flatMap((page) => page.data) ?? [],
  );

  return {
    rows,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
  };
}
